import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'

import type {
  TFollowUpEvaluationDocumentStates,
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'
import type { LangkahObservasi } from '../constants/tahapanPelaksanaan'

export type LangkahObservasiState =
  | 'draft'
  | 'done'
  | 'unlocked'
  | 'locked'
  | ''

export interface LangkahObservasiCardProps extends LangkahObservasi {
  progressPercentage?: number
  isLoading?: boolean
  isAppealed?: boolean
  overrideState?: LangkahObservasiState
  ptkId?: string
  npsn?: string
  onClickEdit?: Function
  state?: TStatePelaksanaan
  appealState?: TPelaksanaanAppealableStep
  latestFormState?: TFollowUpEvaluationDocumentStates
  currentForm?: TPelaksanaanAppealableStep
}
export function LangkahObservasiCard({
  imageSrc,
  callToAction,
  description,
  CtaButtonProps,
  url,
  isLoading = false,
  overrideState = '',
  editCtaText,
  lockedText,
  onClickEdit,
  isAppealed,
  appealState,
  currentForm,
}: LangkahObservasiCardProps) {
  const toast = useToast()
  const router = useRouter()

  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const STATUS_ICON = {
    draft: (
      <Icon fontSize="lg" className="!text-blue-40" color="informational">
        lock_open
      </Icon>
    ),
    unlocked: (
      <Icon fontSize="lg" className="!text-blue-40" color="informational">
        lock_open
      </Icon>
    ),
    locked: (
      <Icon fontSize="lg" color="disabled">
        lock_outlined
      </Icon>
    ),
    done: (
      <Icon fontSize="lg" color="success">
        check_circle_outlined
      </Icon>
    ),
  }

  let CtaButton = null

  if (CtaButtonProps?.[overrideState]) {
    if (overrideState) {
      CtaButton = (
        <Button
          {...CtaButtonProps[overrideState]}
          isLoading={isLoading}
          disabled={isLoading || CtaButtonProps[overrideState]?.disabled}
          onClick={() => {
            if (CtaButtonProps.event) {
              trackEvent({
                activity_type: CtaButtonProps.event.activityType,
                screen_name: CtaButtonProps.event.screenName,
                attributes: CtaButtonProps.event.attributes,
                event_trigger: CtaButtonProps.event.eventTrigger,
              })
            }

            router.push(url)
          }}
          size="lg"
        />
      )
    }
  }

  const RenderButtons = () => {
    if (isAppealed)
      return (
        <div className="flex gap-1 items-center py-3">
          <Icon color="informational" className="motion-safe:animate-spin">
            hourglass_empty
          </Icon>
          <Text className="!text-blue-60" color="informational">
            Perubahan sedang diajukan.
          </Text>
        </div>
      )

    const isEditDisabled =
      isLoading || (!!appealState && appealState !== currentForm)
    const isLocked = overrideState === 'locked'

    return (
      <div className="flex flex-row gap-3">
        {/* Render lockedText if the lockedText is exist and the state is 'locked' */}
        {isLocked && lockedText ? (
          <div className="flex items-center space-x-1 mt-3">
            <Icon
              fontSize="sm"
              color="informational"
              className="!leading-5 !text-xl"
            >
              lock
            </Icon>
            <Text variant="body-sm" className="!text-blue-60">
              {lockedText}
            </Text>
          </div>
        ) : (
          <div
            onClick={() => {
              // natural locked
              if (overrideState === 'locked' && !appealState) {
                toast({
                  message: 'Lakukan sesuai urutan, ya.',
                })
              }
              // locked because of appeal state
              if (overrideState === 'locked' && appealState) {
                toast({
                  message:
                    'Pengajuan perubahan harus diselesaikan terlebih dahulu. Mohon kabari atasan.',
                })
              }
            }}
          >
            {CtaButton}
          </div>
        )}
        {editCtaText && overrideState === 'done' && (
          <Button
            variant="ghost"
            color="blue"
            disabled={isEditDisabled}
            className={clsx(
              'gap-1 !bg-white',
              isEditDisabled && '!text-blue-40'
            )}
            onClick={() => onClickEdit()}
          >
            <Icon color="inherit">edit_outline</Icon>
            {editCtaText}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="aspect-[4/3] h-80 max-w-sm border border-solid rounded-lg">
      <div className="flex flex-col p-6 space-y-4">
        <div className="flex justify-between">
          <Image
            className={clsx(
              'inline self-center flex-none',
              overrideState == 'locked' && 'grayscale'
            )}
            width={84}
            height={84}
            src={imageSrc}
            alt=""
            layout="fixed"
          />
          {STATUS_ICON[overrideState]}
        </div>
        <div className="flex flex-col space-y-1">
          <Text
            variant="heading-sm"
            dangerouslySetInnerHTML={{ __html: callToAction }}
          />
          <Text
            variant="body"
            color="subdued"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
        <RenderButtons />
      </div>
    </div>
  )
}
